// Review & Grade: the class's attempts at its assignments. "To review" is the teacher's
// queue (unreleased, oldest first), each row with the AI's suggested score; "Released" is
// what students can already see. A row opens the attempt's review page.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import { useMediaQuery } from '../../hooks/use-media-query';
import { attemptState, formatScore, teacherScore } from '../../utils/grading';
import { AttemptListStatus, AttemptSummary, fetchClassAttempts } from '../../services/classService';
import {
  cardStyle,
  errorStyle,
  hintStyle,
  linkButtonStyle,
  sectionHeadingStyle,
  statusBadgeStyle,
  tableStyle,
  tdStyle,
  thStyle,
} from './classesStyles';

const logger = createLogger('ClassReviewSection');

const FILTERS: AttemptListStatus[] = ['to_review', 'released'];

/** Below this width the table becomes a list of cards, so Review is never off-screen. */
const NARROW_QUERY = '(max-width: 640px)';

const cardRowStyle: React.CSSProperties = { borderTop: '1px solid #e2e8f0', padding: '0.75rem 0', overflowWrap: 'anywhere' };

interface Props {
  classId: string;
}

const ClassReviewSection: React.FC<Props> = ({ classId }) => {
  const { t } = useTranslation();
  const narrow = useMediaQuery(NARROW_QUERY);
  const [filter, setFilter] = useState<AttemptListStatus>('to_review');
  const [attempts, setAttempts] = useState<AttemptSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped on every load, so a slower answer for an earlier filter can never overwrite
  // the list the teacher switched to.
  const request = useRef(0);

  const load = useCallback(async () => {
    const id = ++request.current;
    setError(null);
    setAttempts(null);
    try {
      const page = await fetchClassAttempts(classId, filter);
      if (id !== request.current) return;
      setAttempts(page.items);
      setTotal(page.total);
    } catch (err) {
      if (id !== request.current) return;
      logger.error('Could not load attempts', err);
      setError(t('classReview.couldNotLoad'));
    }
  }, [classId, filter, t]);

  useEffect(() => {
    load();
  }, [load]);

  // A long queue comes a page at a time; the rest is one click away, never cut off.
  const loadMore = async () => {
    if (!attempts) return;
    const id = request.current;
    setLoadingMore(true);
    try {
      const page = await fetchClassAttempts(classId, filter, attempts.length);
      if (id !== request.current) return;
      setAttempts([...attempts, ...page.items]);
      setTotal(page.total);
    } catch (err) {
      logger.error('Could not load more attempts', err);
      setError(t('classReview.couldNotLoad'));
    } finally {
      setLoadingMore(false);
    }
  };

  const reviewLink = (a: AttemptSummary) => (
    <Link to={`/classes/${classId}/attempts/${a.id}`} style={{ ...linkButtonStyle, textDecoration: 'none' }}>
      {attemptState(a) === 'released' ? t('classReview.view') : t('classReview.review')}
    </Link>
  );
  const practised = (a: AttemptSummary) =>
    new Date(a.started_at).toLocaleDateString(uiLocale(), { month: 'short', day: 'numeric' });
  const badge = (a: AttemptSummary) => {
    const state = attemptState(a);
    return <span style={statusBadgeStyle(state === 'released')}>{t(`classReview.states.${state}`)}</span>;
  };

  return (
    <section id="tour-class-review" style={cardStyle} aria-labelledby="class-review-heading">
      <h2 id="class-review-heading" style={sectionHeadingStyle}>
        {t('classReview.title')}
      </h2>
      <p style={{ ...hintStyle, marginTop: -6, marginBottom: 12 }}>{t('classReview.subtitle')}</p>
      {/* Two toggle buttons, not a tab widget: tabs promise arrow keys and a tabpanel. */}
      <div role="group" aria-label={t('classReview.filterLabel')} style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
        {FILTERS.map(key => (
          <button
            key={key}
            type="button"
            aria-pressed={filter === key}
            style={{ ...linkButtonStyle, textDecoration: filter === key ? 'underline' : 'none', color: filter === key ? undefined : '#64748b' }}
            onClick={() => setFilter(key)}
          >
            {t(`classReview.filters.${key}`)}
          </button>
        ))}
      </div>
      {error && (
        <div style={errorStyle} role="alert">
          {error}{' '}
          <button type="button" style={linkButtonStyle} onClick={load}>
            {t('common.retry')}
          </button>
        </div>
      )}
      {!attempts && !error && <p style={{ ...hintStyle, margin: 0 }}>{t('classReview.loadingList')}</p>}
      {attempts && attempts.length === 0 && (
        <p style={{ ...hintStyle, margin: 0 }}>{t(filter === 'to_review' ? 'classReview.noneToReview' : 'classReview.noneReleased')}</p>
      )}
      {attempts && attempts.length > 0 && narrow && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {attempts.map(a => (
            <li key={a.id} data-testid="class-attempt" style={cardRowStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <strong>{a.student_name || a.student_id}</strong>
                {badge(a)}
              </div>
              <p style={{ ...hintStyle, margin: '2px 0 4px' }}>
                {[a.assignment_title, t('classReview.attemptN', { attempt: a.attempt_no }), practised(a)].join(' · ')}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: '0.85rem' }}>
                <span>
                  {t('classReview.aiSuggested')}: {formatScore(a.suggested_score, a.max_score)} · {t('classReview.grade')}:{' '}
                  {formatScore(teacherScore(a), a.max_score)}
                </span>
                {reviewLink(a)}
              </div>
            </li>
          ))}
        </ul>
      )}
      {attempts && attempts.length > 0 && !narrow && (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>{t('classReview.student')}</th>
                <th style={thStyle}>{t('classReview.assignment')}</th>
                <th style={thStyle}>{t('classReview.attempt')}</th>
                <th style={thStyle}>{t('classReview.practised')}</th>
                <th style={thStyle}>{t('classReview.aiSuggested')}</th>
                <th style={thStyle}>{t('classReview.grade')}</th>
                <th style={thStyle}>{t('common.status')}</th>
                <th style={thStyle} aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {attempts.map(a => (
                <tr key={a.id} data-testid="class-attempt">
                  <td style={{ ...tdStyle, overflowWrap: 'anywhere' }}>{a.student_name || a.student_id}</td>
                  <td style={{ ...tdStyle, overflowWrap: 'anywhere' }}>
                    {a.assignment_title}
                    {a.content_name && <span style={{ ...hintStyle, display: 'block', margin: 0 }}>{a.content_name}</span>}
                  </td>
                  <td style={tdStyle}>{a.attempt_no}</td>
                  <td style={tdStyle}>{practised(a)}</td>
                  <td style={tdStyle}>{formatScore(a.suggested_score, a.max_score)}</td>
                  <td style={tdStyle}>{formatScore(teacherScore(a), a.max_score)}</td>
                  <td style={tdStyle}>{badge(a)}</td>
                  <td style={tdStyle}>{reviewLink(a)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {attempts && attempts.length < total && (
        <button type="button" style={{ ...linkButtonStyle, marginTop: 10 }} disabled={loadingMore} onClick={loadMore}>
          {t('classReview.loadMore', { shown: attempts.length, total })}
        </button>
      )}
    </section>
  );
};

export default ClassReviewSection;
